// ---------------------------------------------------------------------------
// AG-028 Phase 5 — Agency Marketplace / Franchise Package CQRS Event Catalog
// ---------------------------------------------------------------------------
// Story AG-028: "Agency marketplace/franchise packages allow agencies to
// package workflows/templates/reports and install/customize for clients."
//
// Phase 1 (LANDED) shipped the pure classifier
// `packages/platform/src/services/marketplace/ag-028-marketplace-package-classifier.ts`
// with closed `MARKETPLACE_PACKAGE_KINDS` (4) + `MARKETPLACE_INSTALLABILITY` (3) +
// `MARKETPLACE_CUSTOMISATION_POLICIES` (3) + `MARKETPLACE_PACKAGE_BLOCKERS` (11).
// The Phase 1 header (lines 28-30) pins the canonical Phase 5 events:
//   - `agency.marketplace_package_classified@v1`
//   - `agency.marketplace_package_installed@v1`
//
// This file is the Phase 5 narrow follow-up: it catalogues those two event
// types at the database tier so the future Phase 3 submit + install routes,
// the Phase 4 agency-portal UI, and downstream materializers/DLQ
// consumers/audit-runner read them without taking a runtime dependency on
// `@ease/platform`. Per Governance Rule #7 ("Event Catalog"): every event
// type catalogued BEFORE Phase 3 runtime wiring so emitters import the
// constants instead of hand-rolling drift-prone strings.
//
// What this file is NOT:
//  - It does not emit events — that's Phase 3 (route wiring).
//  - It does not declare the `agency_marketplace_packages` Drizzle table
//    (Phase 2).
//  - It does not change the Phase 1 classifier contract (additive only).
//
// What it IS:
//  - The single source of truth for `agency.marketplace_package_*` event
//    strings.
//  - Typed payload contracts so emitters + DLQ consumers cannot drift.
//  - Runtime narrowing guards so DLQ/audit-runner reject unknown event types.
// ---------------------------------------------------------------------------

/**
 * Closed catalog of the 2 AG-028 CQRS event types. Versioned with the
 * `@v1` suffix per platform convention so a future shape change ships a
 * `@v2` alongside (never mutate in place).
 *
 * One business fact per event (Governance Rule #6):
 *  - `classified` — classifier landed an installability verdict on a draft
 *    package (submit-time event).
 *  - `installed` — classifier landed an install verdict for a specific
 *    client tenant (install-time event).
 */
export const AG_028_AGENCY_MARKETPLACE_EVENT_TYPES = [
  "agency.marketplace_package_classified@v1",
  "agency.marketplace_package_installed@v1",
] as const;

export type Ag028AgencyMarketplaceEventType =
  (typeof AG_028_AGENCY_MARKETPLACE_EVENT_TYPES)[number];

/**
 * Closed catalog of package kinds — mirrors Phase 1
 * `MARKETPLACE_PACKAGE_KINDS` verbatim. Used by `classified@v1` payload.
 */
export const AG_028_AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB = [
  "workflow_bundle",
  "template_bundle",
  "report_bundle",
  "mixed_franchise",
] as const;

export type Ag028AgencyMarketplacePackageKindVocab =
  (typeof AG_028_AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB)[number];

/**
 * Closed catalog of installability verdicts — mirrors Phase 1
 * `MARKETPLACE_INSTALLABILITY` verbatim. Drives the Phase 3 route 200 /
 * 409 / 422 branching.
 */
export const AG_028_AGENCY_MARKETPLACE_INSTALLABILITY_VOCAB = [
  "installable",
  "blocked",
  "requires_review",
] as const;

export type Ag028AgencyMarketplaceInstallabilityVocab =
  (typeof AG_028_AGENCY_MARKETPLACE_INSTALLABILITY_VOCAB)[number];

/**
 * Closed catalog of customisation policies — mirrors Phase 1
 * `MARKETPLACE_CUSTOMISATION_POLICIES` verbatim. Carried on `classified@v1`
 * so the Phase 4 UI can render the customisation-affordance label.
 */
export const AG_028_AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB = [
  "locked",
  "branding_only",
  "full_override",
] as const;

export type Ag028AgencyMarketplaceCustomisationPolicyVocab =
  (typeof AG_028_AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB)[number];

/**
 * Closed catalog of blocker rules — mirrors Phase 1
 * `MARKETPLACE_PACKAGE_BLOCKERS` verbatim. Phase 3 surfaces the rule key
 * as the error code; Phase 4 UI renders the rule key as the human label.
 */
export const AG_028_AGENCY_MARKETPLACE_BLOCKER_VOCAB = [
  "empty_workflows_and_templates_and_reports",
  "name_too_long",
  "name_empty",
  "version_invalid_semver",
  "owner_agency_missing",
  "owner_not_an_agency",
  "non_agency_dependency",
  "circular_dependency",
  "client_overrides_locked_artifact",
  "kind_mismatch_for_mixed_franchise",
  "kind_mismatch_for_single_bundle",
] as const;

export type Ag028AgencyMarketplaceBlockerVocab =
  (typeof AG_028_AGENCY_MARKETPLACE_BLOCKER_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                    */
/* -------------------------------------------------------------------------- */

export function isAg028AgencyMarketplaceEventType(
  value: unknown,
): value is Ag028AgencyMarketplaceEventType {
  return (
    typeof value === "string" &&
    (AG_028_AGENCY_MARKETPLACE_EVENT_TYPES as readonly string[]).includes(
      value,
    )
  );
}

export function isAg028AgencyMarketplacePackageKindVocab(
  value: unknown,
): value is Ag028AgencyMarketplacePackageKindVocab {
  return (
    typeof value === "string" &&
    (
      AG_028_AGENCY_MARKETPLACE_PACKAGE_KIND_VOCAB as readonly string[]
    ).includes(value)
  );
}

export function isAg028AgencyMarketplaceInstallabilityVocab(
  value: unknown,
): value is Ag028AgencyMarketplaceInstallabilityVocab {
  return (
    typeof value === "string" &&
    (
      AG_028_AGENCY_MARKETPLACE_INSTALLABILITY_VOCAB as readonly string[]
    ).includes(value)
  );
}

export function isAg028AgencyMarketplaceCustomisationPolicyVocab(
  value: unknown,
): value is Ag028AgencyMarketplaceCustomisationPolicyVocab {
  return (
    typeof value === "string" &&
    (
      AG_028_AGENCY_MARKETPLACE_CUSTOMISATION_POLICY_VOCAB as readonly string[]
    ).includes(value)
  );
}

export function isAg028AgencyMarketplaceBlockerVocab(
  value: unknown,
): value is Ag028AgencyMarketplaceBlockerVocab {
  return (
    typeof value === "string" &&
    (AG_028_AGENCY_MARKETPLACE_BLOCKER_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contracts                                                 */
/* -------------------------------------------------------------------------- */

export interface Ag028AgencyMarketplaceBaseEventPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /** FK into the future `agency_marketplace_packages` table (Phase 2). */
  packageId: string;
  /** Owner agency tenant (the seller). */
  ownerAgencyId: string;
}

export interface Ag028AgencyMarketplaceClassifiedPayload
  extends Ag028AgencyMarketplaceBaseEventPayload {
  /** Phase 1 package kind. */
  packageKind: Ag028AgencyMarketplacePackageKindVocab;
  /** Phase 1 installability verdict (drives Phase 3 status code). */
  installability: Ag028AgencyMarketplaceInstallabilityVocab;
  /** Phase 1 customisation policy carried for the Phase 4 UI label. */
  customisationPolicy: Ag028AgencyMarketplaceCustomisationPolicyVocab;
  /**
   * Phase 1 blocker rules that fired. Empty when `installability` is
   * `installable`.
   */
  blockers: readonly Ag028AgencyMarketplaceBlockerVocab[];
  /** Operator who submitted (audit). */
  submitterUserId: string;
  /** ISO-8601 — when the classification finalised. */
  classifiedAt: string;
}

export interface Ag028AgencyMarketplaceInstalledPayload
  extends Ag028AgencyMarketplaceBaseEventPayload {
  /** Per-client install destination. */
  clientTenantId: string;
  /** Phase 1 install verdict for this client. */
  installability: Ag028AgencyMarketplaceInstallabilityVocab;
  /**
   * Per-install blocker rules. Empty when `installability` is
   * `installable` (e.g. client overrides honoured the customisation policy).
   */
  blockers: readonly Ag028AgencyMarketplaceBlockerVocab[];
  /** Operator who initiated the install (audit). */
  installerUserId: string;
  /** ISO-8601 — when the install verdict finalised. */
  installedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (route emitter, materializer, DLQ, audit-runner) `switch (event.type)`
 * and pull the right payload without any-casting.
 */
export type Ag028AgencyMarketplaceEvent =
  | {
      type: "agency.marketplace_package_classified@v1";
      payload: Ag028AgencyMarketplaceClassifiedPayload;
    }
  | {
      type: "agency.marketplace_package_installed@v1";
      payload: Ag028AgencyMarketplaceInstalledPayload;
    };
